/**
 * Patient Education Guide content — bilingual EN/ES (Luisa R1, Jun 11 2026).
 *
 * Source of truth: Luisa Parnell's submission, captured verbatim in
 * docs/roles/luisa_patient_education_2026-06-10.md. This is vetted,
 * patient-facing copy — treat it like the legal templates: edits here are
 * content changes, never AI paraphrase. The disclaimer in particular must
 * stay word-for-word.
 *
 * Shared by the public education page (LeadEducationGuidePage.js) and any
 * future proposal-document embed. Pure data — no imports, no PHI.
 */
"use strict";

export const EDUCATION_GUIDE = {
  en: {
    langLabel: "English",
    title: "Patient Education Guide — Zignagenix Regenerative Medicine",
    sections: [
      {
        h: "Who We Are",
        p: "At Zignagenix, we are both the source and the provider of our regenerative therapies. We own and operate our own laboratory, allowing us to work directly with live, fresh regenerative products rather than frozen products or products purchased from outside distributors. This provides greater transparency, quality control, and consistency in patient care.",
      },
      {
        h: "Our Regenerative Therapies",
        p: "We offer advanced regenerative therapies tailored to each patient's individual needs:",
        list: [
          "Umbilical Cord Mesenchymal Stem Cells (MSCs)",
          "Natural Killer (NK) Cell Therapy",
          "Dr. Mari Dezawa's Muse Cells",
          "Exosomes",
          "PRP (Platelet-Rich Plasma)",
          "Peptide Therapy",
        ],
      },
      {
        h: "Umbilical Cord Stem Cells (MSCs)",
        p: "We specialize in umbilical cord-derived mesenchymal stem cells obtained from healthy, carefully screened donors. These cells are highly active and function as signaling cells that help support the body's natural repair processes.",
        list: [
          "Reduce inflammation",
          "Support tissue repair and regeneration",
          "Promote healthy blood vessel formation (angiogenesis)",
          "Support immune balance",
          "Enhance recovery and healing",
        ],
      },
      {
        h: "How Stem Cells Work",
        p: "As we age, the number and activity of our own stem cells naturally decline. Regenerative therapies help support the body's natural healing response by sending signals that encourage repair, reduce inflammation, and promote healthy cellular function.",
      },
      {
        h: "Natural Killer (NK) Cell Therapy",
        p: "Natural Killer cells are specialized immune cells that serve as one of the body's first lines of defense. They naturally identify and eliminate abnormal cells. NK Cell Therapy may help support immune surveillance, immune system function, overall wellness, and the body's ability to recognize abnormal cells.",
      },
      {
        h: "Dr. Mari Dezawa's Muse Cells",
        p: "Muse Cells (Multilineage-differentiating Stress-Enduring Cells) are a unique type of regenerative cell developed through the pioneering work of Dr. Mari Dezawa. They have demonstrated the ability to naturally home to areas of injury and participate in tissue repair. Potential benefits may include support for neurological health, tissue repair, complex degenerative conditions, and cellular regeneration.",
      },
      {
        h: "What Are Exosomes?",
        p: "Exosomes are microscopic messenger particles naturally released by cells. They carry proteins, growth factors, and signaling molecules that improve communication between cells. Exosomes may help support tissue repair, reduce inflammation, enhance cellular communication, and promote recovery.",
      },
      {
        h: "What Is PRP?",
        p: "Platelet-Rich Plasma (PRP) is obtained from your own blood and contains concentrated platelets rich in growth factors. PRP may help support healing responses, tissue repair, recovery after injury, and joint and soft tissue health.",
      },
      {
        h: "Peptide Therapy",
        p: "Peptides are short chains of amino acids that naturally occur in the body and act as messengers to regulate important biological functions. Depending on the peptide selected and physician recommendations, peptide therapy may support healthy aging, recovery and healing, muscle maintenance, metabolic health, energy levels, sleep quality, cognitive wellness, immune function, and weight management and body composition.",
        list: [
          "BPC-157 — Supports tissue recovery and healing",
          "TB-500 (Thymosin Beta-4) — Supports mobility and recovery",
          "NAD+ — Supports cellular energy and healthy aging",
          "CJC-1295 / Ipamorelin — Supports natural growth hormone release and recovery",
          "Tirzepatide — Supports weight management and metabolic health through GLP-1/GIP receptor activity",
          "Additional peptide protocols may be recommended based on individual goals.",
        ],
      },
      {
        h: "Treatment Locations",
        p: "We proudly offer treatments at three clinic locations in Mexico: Tijuana, Cancún, and Puerto Vallarta. Depending on your physician's recommendations and travel preferences, we can coordinate your care at the location that best meets your needs.",
      },
      {
        h: "How Treatments Are Administered",
        p: "Treatment methods vary based on the individualized care plan and may include IV infusions, localized injections, PRP applications, exosome administration, and peptide protocols.",
      },
      {
        h: "What to Expect",
        p: "Some patients may experience temporary fatigue, mild flu-like symptoms, or soreness at injection sites during the first 24–48 hours. Most patients resume normal activities shortly after treatment, although your physician may recommend temporary activity modifications.",
      },
      {
        h: "Cost and Value",
        p: "Treatment recommendations and pricing vary depending on the therapies selected and each patient's individual needs. During your complimentary consultation, the physician will review your medical history and provide a personalized treatment recommendation.",
      },
      {
        h: "Next Steps",
        p: "Your physician consultation is complimentary and designed to help determine the treatment plan best suited to your goals and health needs. Our mission is to educate, support, and empower you to make informed decisions about your health and quality of life.",
      },
      {
        h: "Important Medical Disclaimer",
        disclaimer: true,
        p: "All regenerative therapies offered by Zignagenix, including stem cells, NK Cells, Muse Cells, exosomes, PRP, and peptide therapies, are provided only after physician evaluation and recommendation. Every patient is unique, and treatment plans are individualized based on medical history, health goals, and clinical assessment. While many patients report improvements in symptoms, function, and quality of life, individual responses and outcomes vary. No specific results can be guaranteed. These therapies are not intended to diagnose, treat, cure, or prevent any disease. Patients should continue following the recommendations of their primary healthcare providers.",
      },
    ],
  },
  es: {
    langLabel: "Español",
    title: "Guía Educativa para Pacientes — Medicina Regenerativa Zignagenix",
    sections: [
      {
        h: "Quiénes Somos",
        p: "En Zignagenix, somos tanto la fuente como los proveedores de nuestras terapias regenerativas. Contamos con nuestro propio laboratorio, lo que nos permite trabajar directamente con productos regenerativos vivos y frescos, en lugar de productos congelados o adquiridos a distribuidores externos. Esto nos brinda mayor transparencia, control de calidad y consistencia en la atención al paciente.",
      },
      {
        h: "Nuestras Terapias Regenerativas",
        p: "Ofrecemos terapias regenerativas avanzadas adaptadas a las necesidades individuales de cada paciente:",
        list: [
          "Células Madre Mesenquimales (MSC) derivadas del cordón umbilical",
          "Terapia con células Natural Killer (NK)",
          "Células Muse de la Dra. Mari Dezawa",
          "Exosomas",
          "PRP (Plasma Rico en Plaquetas)",
          "Terapia con Péptidos",
        ],
      },
      {
        h: "Células Madre Umbilicales (MSC)",
        p: "Nos especializamos en células madre mesenquimales derivadas del cordón umbilical obtenidas de donantes sanos y cuidadosamente seleccionados. Estas células son altamente activas y funcionan como células señalizadoras que ayudan a apoyar los procesos naturales de reparación del organismo.",
        list: [
          "Reducir la inflamación",
          "Favorecer la reparación y regeneración de tejidos",
          "Promover la formación saludable de vasos sanguíneos (angiogénesis)",
          "Apoyar el equilibrio del sistema inmunológico",
          "Favorecer la recuperación y la sanación",
        ],
      },
      {
        h: "Cómo Funcionan las Células Madre",
        p: "A medida que envejecemos, la cantidad y actividad de nuestras propias células madre disminuyen naturalmente. Las terapias regenerativas ayudan a apoyar la respuesta natural de sanación del organismo mediante señales que favorecen la reparación, reducen la inflamación y promueven una función celular saludable.",
      },
      {
        h: "Terapia con Células Natural Killer (NK)",
        p: "Las células Natural Killer (NK) son células especializadas del sistema inmunológico que constituyen una de las primeras líneas de defensa del organismo. Identifican y eliminan de forma natural células anormales. La terapia con células NK puede ayudar a apoyar la vigilancia inmunológica, la función del sistema inmunológico, el bienestar general y la capacidad del organismo para reconocer células anormales.",
      },
      {
        h: "Células Muse de la Dra. Mari Dezawa",
        p: "Las células Muse (Multilineage-differentiating Stress-Enduring Cells) son un tipo único de célula regenerativa desarrolladas a partir del trabajo pionero de la Dra. Mari Dezawa. Han demostrado la capacidad de localizar naturalmente áreas lesionadas y participar en la reparación tisular. Sus beneficios potenciales pueden incluir apoyo para la salud neurológica, la reparación de tejidos, condiciones degenerativas complejas y la regeneración celular.",
      },
      {
        h: "¿Qué Son los Exosomas?",
        p: "Los exosomas son partículas mensajeras microscópicas liberadas naturalmente por las células. Transportan proteínas, factores de crecimiento y moléculas señalizadoras que mejoran la comunicación celular. Los exosomas pueden ayudar a favorecer la reparación de tejidos, reducir la inflamación, mejorar la comunicación celular y promover la recuperación.",
      },
      {
        h: "¿Qué es el PRP?",
        p: "El Plasma Rico en Plaquetas (PRP) se obtiene de la propia sangre del paciente y contiene una alta concentración de plaquetas ricas en factores de crecimiento. El PRP puede ayudar a apoyar los procesos de sanación, la reparación de tejidos, la recuperación después de lesiones y la salud articular y de tejidos blandos.",
      },
      {
        h: "Terapia con Péptidos",
        p: "Los péptidos son cadenas cortas de aminoácidos presentes naturalmente en el organismo y actúan como mensajeros que regulan importantes funciones biológicas. Dependiendo del péptido seleccionado y de las recomendaciones médicas, la terapia con péptidos puede apoyar el envejecimiento saludable, la recuperación y sanación, el mantenimiento muscular, la salud metabólica, los niveles de energía, la calidad del sueño, el bienestar cognitivo, la función inmunológica y el control del peso y la composición corporal.",
        list: [
          "BPC-157 — Apoya la recuperación y sanación de tejidos",
          "TB-500 (Timosina Beta-4) — Favorece la movilidad y recuperación",
          "NAD+ — Apoya la energía celular y el envejecimiento saludable",
          "CJC-1295 / Ipamorelin — Favorecen la liberación natural de hormona del crecimiento y la recuperación",
          "Tirzepatida — Apoya el control del peso y la salud metabólica mediante la actividad sobre los receptores GLP-1/GIP",
          "Otros protocolos pueden recomendarse según los objetivos individuales.",
        ],
      },
      {
        h: "Ubicaciones del Tratamiento",
        p: "Ofrecemos tratamientos en tres ubicaciones en México: Tijuana, Cancún y Puerto Vallarta. Dependiendo de las recomendaciones médicas y sus preferencias de viaje, coordinaremos su atención en la ubicación que mejor se adapte a sus necesidades.",
      },
      {
        h: "Cómo se Administran los Tratamientos",
        p: "Los métodos de administración varían según el plan individualizado y pueden incluir infusiones intravenosas, inyecciones localizadas, aplicaciones de PRP, administración de exosomas y protocolos con péptidos.",
      },
      {
        h: "Qué Esperar",
        p: "Algunos pacientes pueden experimentar fatiga temporal, síntomas leves similares a la gripe o molestias en las áreas de aplicación durante las primeras 24–48 horas. La mayoría reanuda sus actividades normales poco después del tratamiento, aunque el médico puede recomendar modificaciones temporales.",
      },
      {
        h: "Costo y Valor",
        p: "Las recomendaciones y costos varían según las terapias seleccionadas y las necesidades individuales de cada paciente. Durante la consulta gratuita, el médico revisará su historial clínico y proporcionará una recomendación personalizada.",
      },
      {
        h: "Próximos Pasos",
        p: "La consulta médica es gratuita y está diseñada para determinar el plan de tratamiento más adecuado para sus objetivos y necesidades de salud. Nuestra misión es educarlo, apoyarlo y brindarle las herramientas necesarias para tomar decisiones informadas sobre su salud y calidad de vida.",
      },
      {
        h: "Aviso Médico Importante",
        disclaimer: true,
        p: "Todas las terapias regenerativas ofrecidas por Zignagenix, incluyendo células madre, células NK, células Muse, exosomas, PRP y terapias con péptidos, se proporcionan únicamente después de una evaluación y recomendación médica. Cada paciente es único, y los planes de tratamiento se individualizan según sus antecedentes médicos, objetivos de salud y evaluación clínica. Aunque muchos pacientes reportan mejorías en síntomas, funcionalidad y calidad de vida, las respuestas y resultados pueden variar. No se pueden garantizar resultados específicos. Estas terapias no están destinadas a diagnosticar, tratar, curar ni prevenir ninguna enfermedad. Los pacientes deben continuar siguiendo las recomendaciones de sus proveedores de atención médica habituales.",
      },
    ],
  },
};
